import type { ReactNode } from 'react'
import { useState } from 'react'
import '../../components/settings-ui.css'

/**
 * One titled block inside a Settings / You section page; `id` doubles as the deep-link anchor.
 * The head is a small-caps label (design-language §2), an optional muted `count` beside it and an
 * optional right-aligned `note` (`text-2`), then `action`. `hideTitle` keeps the heading for
 * assistive tech when the page title already says it (Settings › Machines).
 */
export function Section({
  id,
  title,
  count,
  note,
  action,
  hideTitle,
  children,
}: {
  id: string
  title: string
  count?: ReactNode
  note?: ReactNode
  action?: ReactNode
  hideTitle?: boolean
  children: ReactNode
}) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="st-sec scroll-mt-20">
      {hideTitle ? (
        <h2 id={`${id}-h`} className="sr-only">
          {title}
        </h2>
      ) : (
        <div className="st-sec__head">
          <h2 id={`${id}-h`} className="st-sec__title">
            {title}
          </h2>
          {count !== undefined ? <span className="st-sec__count">{count}</span> : null}
          <span className="flex-1" />
          {note ? <span className="st-sec__note">{note}</span> : null}
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

/** Long lists show the first `limit` rows; the rest sit behind a count-bearing button. */
export function ShowMore<T>({
  items,
  limit = 5,
  label,
  children,
}: {
  items: readonly T[]
  limit?: number
  /** Plural noun for the button, e.g. "branches". */
  label: string
  children: (visible: readonly T[]) => ReactNode
}) {
  const [all, setAll] = useState(false)
  const hidden = items.length - limit
  const visible = all || hidden <= 0 ? items : items.slice(0, limit)
  return (
    <>
      {children(visible)}
      {hidden > 0 ? (
        <div className="px-4 pt-1">
          <button
            type="button"
            className="vk-btn vk-btn--ghost vk-btn--sm"
            aria-expanded={all}
            onClick={() => setAll((v) => !v)}
          >
            {all ? `Show fewer ${label}` : `Show ${hidden} more ${label}`}
          </button>
        </div>
      ) : null}
    </>
  )
}
